import { StreamStates, avSelectors, useAvContext } from "@minervaproject/avsubsystem";
import { useCallback } from "react";

import { createDeviceToast, createErrorToast } from "components/shared/toasts";
import { useCapabilities, useUserIdsWithCapability } from "hooks/capabilities";
import { useAppDispatch, useAppSelector } from "hooks/redux_hooks";
import { useHasClassStarted } from "hooks/use_has_class_started";
import { useKeyHold } from "hooks/use_key_hold";
import { useKeyPress } from "hooks/use_key_press";
import { USER_PICKER_PROPERTIES, useUsersProperties } from "hooks/use_user_properties";
import {
  clearAllSignals,
  clearAllStagePanes,
  sendPubsubMessage,
  setAllStagePanes,
  setStagePane,
  setUserIsSpeaking,
  setUserReaction,
  setUserVote,
  toggleUserHandRaised,
} from "shared/reducers/classroom";
import {
  CapabilityType,
  KeyboardShortcutSignalType,
  LocalModalContentType,
  PaneContentType,
  PubsubMessageTypeType,
  RecordingStateType,
  SidebarPanelType,
} from "shared/types";
import { ReactionType } from "shared/types/user";
import { isUserOnStage } from "shared/utils/stage";
import { classroomSelectors, localSelectors } from "store";
import {
  setKeyboardShortcutSignal,
  setSidebarPanel,
  showLocalModal,
  toggleSidebarPanel,
} from "store/reducers/local";
import { selectEffectivePttMode } from "store/selectors/classroom";
import { buildStagePanesForLayoutChange, findBestPaneIndex } from "utils/stage";

const POSSIBLE_KEYS = [
  // SIGNALS
  "y",
  "n",
  "h",
  "r",
  // STAGE PANES
  "1",
  "2",
  "3",
  "4",
  "5",
  "6",
  "7",
  "8",
  // SIDEBAR
  "/",
  "b",
  "c",
  "d",
  "a",
  "Escape",
  // TIMELINE
  ".",
  ",",
  "p",
  // OTHER
  "e",
  "i",
  "k",
  "m",
  "s",
  "`",
];
const POSSIBLE_ALT_KEYS = ["t"];
const POSSIBLE_SHIFT_KEYS = [
  // REACTIONS
  "1",
  "2",
  "3",
  "4",
  "5",
  "6",
  "7",
  "8",
];
const REACTION_KEYS_MAP: { [key: string]: string } = {
  "1": "like",
  "2": "dislike",
  "3": "agree",
  "4": "disagree",
  "5": "laugh",
  "6": "wow",
  "7": "love",
  "8": "clap",
};
const VOTE_KEYS_MAP: { [key: string]: string } = {
  y: "yes",
  n: "no",
};

function KeyboardShortcutsController({ userId }: { userId: string }) {
  const dispatch = useAppDispatch();
  const { canControlClassroom, isVisibleInClass } = useCapabilities();
  const currentPanel = useAppSelector(localSelectors.selectSidebarPanel());
  const activeBreakoutSessionId = useAppSelector(
    classroomSelectors.selectActiveBreakoutSessionId(),
  );
  const breakoutRoom = useAppSelector(classroomSelectors.selectActiveBreakoutRoomForCurrentUser());
  const isAvEnabled = useAppSelector(classroomSelectors.selectCurrentUserIsAvEnabled());
  const hasVisibleParticipantAv = useAppSelector(
    classroomSelectors.selectCurrentUserhasVisibleParticipantAv(),
  );
  const avConnection = useAvContext();
  const classInfo = useAppSelector(classroomSelectors.selectClassInfo());
  const hasClassStarted = useHasClassStarted(classInfo);
  const pubsubMessage = useAppSelector(localSelectors.selectPubsubMessage());
  const keyboardShortcutSignal = useAppSelector(localSelectors.selectKeyboardShortcutSignal());
  const localModalContent = useAppSelector(localSelectors.selectLocalModalContent());

  const localStreamState = avSelectors.useStreamState(userId);
  const isPublishedStreamReady = localStreamState?.state === StreamStates.playable;

  // Used for stage operations
  const stage = useAppSelector(classroomSelectors.selectStage());
  const users = Object.values(useUsersProperties(USER_PICKER_PROPERTIES));
  const isInPttMode = useAppSelector(selectEffectivePttMode(userId));

  const recordingState = useAppSelector(classroomSelectors.selectRecordingState());
  const isRecording = recordingState === RecordingStateType.Recording;

  const handleRaiseHand = useCallback(() => {
    dispatch(toggleUserHandRaised({ userId }));
  }, [userId, dispatch]);

  const isAlwaysSubscribedToUserIds = useUserIdsWithCapability(
    CapabilityType.IS_ALWAYS_SUBSCRIBED_TO,
  );
  const isVisibleInClassUserIds = useUserIdsWithCapability(CapabilityType.IS_VISIBLE_IN_CLASS);
  const canParticipateInClassUserIds = useUserIdsWithCapability(
    CapabilityType.CAN_PARTICIPATE_IN_CLASS,
  );
  const participantBarUserIds = useAppSelector(classroomSelectors.selectParticipantBarUserIds());
  const isInPerson = useAppSelector(classroomSelectors.selectIsInPersonClass());
  const presentVisibleUsers = users.filter(
    (user) => user.is_present && isVisibleInClassUserIds.includes(user.id),
  );

  useKeyPress(
    POSSIBLE_KEYS,
    POSSIBLE_ALT_KEYS,
    POSSIBLE_SHIFT_KEYS,
    (key) => {
      if (Object.keys(VOTE_KEYS_MAP).includes(key) && isVisibleInClass) {
        dispatch(setUserVote({ userId, vote: VOTE_KEYS_MAP[key] }));
      } else if (key === "h" && isVisibleInClass) {
        dispatch(toggleUserHandRaised({ userId }));
      } else if (key === "r" && isVisibleInClass) {
        if (localModalContent === LocalModalContentType.Reactions) {
          dispatch(showLocalModal({ content: LocalModalContentType.None }));
        } else {
          dispatch(showLocalModal({ content: LocalModalContentType.Reactions }));
        }
      } else if (
        ["1", "2", "3", "4", "5", "6", "7", "8"].includes(key) &&
        (canControlClassroom || !!activeBreakoutSessionId)
      ) {
        if (activeBreakoutSessionId && !breakoutRoom) return;
        const numberOfPanes = parseInt(key, 10);
        let eligibleUserIds: string[] = [];
        if (!isInPerson) {
          eligibleUserIds = breakoutRoom
            ? users
                .filter(
                  (user) =>
                    user.is_present &&
                    (breakoutRoom.assigned_user_ids.includes(user.id) ||
                      breakoutRoom.visiting_user_ids.includes(user.id)),
                )
                .map((user) => user.id)
            : participantBarUserIds;
        }
        const updatedStagePanes = buildStagePanesForLayoutChange(
          breakoutRoom?.panes || stage.panes,
          numberOfPanes,
          presentVisibleUsers,
          eligibleUserIds,
          isAlwaysSubscribedToUserIds,
          canParticipateInClassUserIds,
        );
        window.flightLogger.log(
          `[KeyboardShortcutController] Updating stage panes: ${JSON.stringify(updatedStagePanes)}`,
        );
        dispatch(
          setAllStagePanes({
            panes: updatedStagePanes,
            ownerId: userId,
            breakoutRoomId: breakoutRoom?.id,
          }),
        );
      } else if (key === "/" && canControlClassroom) {
        dispatch(toggleSidebarPanel({ panel: SidebarPanelType.Timeline }));
      } else if (key === "b" && isRecording && !activeBreakoutSessionId) {
        dispatch(showLocalModal({ content: LocalModalContentType.BookmarkForm }));
      } else if (key === "c") {
        dispatch(toggleSidebarPanel({ panel: SidebarPanelType.Chat }));
      } else if (
        key === "Escape" &&
        currentPanel !== SidebarPanelType.None &&
        localModalContent === LocalModalContentType.None &&
        !stage.featured_in_modal &&
        !stage.panes.some((pane) => pane.featured_poll_response)
      ) {
        dispatch(setSidebarPanel({ panel: SidebarPanelType.None }));
      } else if (key === "d") {
        dispatch(toggleSidebarPanel({ panel: SidebarPanelType.Directory }));
      } else if (key === "a" && canControlClassroom) {
        if (currentPanel !== SidebarPanelType.Directory) {
          dispatch(toggleSidebarPanel({ panel: SidebarPanelType.Directory }));
        }
        dispatch(
          setKeyboardShortcutSignal({ signal: KeyboardShortcutSignalType.ToggleAbsentStudents }),
        );
      } else if (key === "." && canControlClassroom && !activeBreakoutSessionId) {
        if (currentPanel !== SidebarPanelType.Timeline) {
          dispatch(toggleSidebarPanel({ panel: SidebarPanelType.Timeline }));
        }
        dispatch(
          setKeyboardShortcutSignal({ signal: KeyboardShortcutSignalType.NextTimelineSegment }),
        );
      } else if (key === "," && canControlClassroom && !activeBreakoutSessionId) {
        if (currentPanel !== SidebarPanelType.Timeline) {
          dispatch(toggleSidebarPanel({ panel: SidebarPanelType.Timeline }));
        }
        dispatch(
          setKeyboardShortcutSignal({ signal: KeyboardShortcutSignalType.PreviousTimelineSegment }),
        );
      } else if (key === "p" && canControlClassroom) {
        if (currentPanel !== SidebarPanelType.Timeline) {
          dispatch(toggleSidebarPanel({ panel: SidebarPanelType.Timeline }));
        }
        dispatch(
          setKeyboardShortcutSignal({ signal: KeyboardShortcutSignalType.ToggleInstructorNotes }),
        );
      } else if (key === "e" && canControlClassroom) {
        if (activeBreakoutSessionId && !breakoutRoom) return;
        dispatch(clearAllStagePanes({ breakoutRoomId: breakoutRoom?.id }));
      } else if (key === "i" && canControlClassroom) {
        dispatch(setKeyboardShortcutSignal({ signal: KeyboardShortcutSignalType.ToggleTimer }));
      } else if (key === "k") {
        if (localModalContent === LocalModalContentType.KeyboardShortcuts) {
          dispatch(showLocalModal({ content: LocalModalContentType.None }));
        } else {
          dispatch(showLocalModal({ content: LocalModalContentType.KeyboardShortcuts }));
        }
      } else if (key === "m" && isAvEnabled && isVisibleInClass) {
        dispatch(setKeyboardShortcutSignal({ signal: KeyboardShortcutSignalType.ToggleMute }));
      } else if (key === "s" && canControlClassroom) {
        dispatch(clearAllSignals());
      } else if (key === "`" && canControlClassroom) {
        // Cannot run confetti if it's already running
        if (
          pubsubMessage?.type === PubsubMessageTypeType.RUN_CONFETTI ||
          keyboardShortcutSignal === KeyboardShortcutSignalType.RunConfetti
        ) {
          return;
        }
        dispatch(
          sendPubsubMessage({
            type: PubsubMessageTypeType.RUN_CONFETTI,
          }),
        );
        dispatch(setKeyboardShortcutSignal({ signal: KeyboardShortcutSignalType.RunConfetti }));
      }
    },
    (key) => {
      if (key === "t" && canControlClassroom && !isInPerson) {
        if (localModalContent === LocalModalContentType.TalkTime) {
          dispatch(showLocalModal({ content: LocalModalContentType.None }));
        } else {
          dispatch(showLocalModal({ content: LocalModalContentType.TalkTime }));
        }
      }
    },
    (key) => {
      if (Object.keys(REACTION_KEYS_MAP).includes(key) && isVisibleInClass) {
        const reaction = REACTION_KEYS_MAP[key] as ReactionType;
        dispatch(setUserReaction({ userId, reaction }));
      }
    },
  );

  useKeyHold(
    ["Control"],
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    (event: Event) => {
      if (!isVisibleInClass || !isAvEnabled || !isInPttMode || !isPublishedStreamReady) return;

      if (!hasVisibleParticipantAv) {
        createDeviceToast(
          "Microphone muted",
          "You must be on screen to speak. Raise your hand to signal to the instructor that you'd like to speak.",
          false,
          "mic",
          handleRaiseHand,
          "Raise Hand",
        );
        return;
      }

      avConnection?.setLocalAudio(true);
      dispatch(setUserIsSpeaking({ userId, isSpeaking: true }));

      const panes = breakoutRoom?.panes || stage.panes;
      if (!isUserOnStage(panes, userId)) {
        const bestPaneIndex = findBestPaneIndex(
          presentVisibleUsers,
          isAlwaysSubscribedToUserIds,
          panes,
          participantBarUserIds,
        );
        if (bestPaneIndex > -1) {
          dispatch(
            setStagePane({
              index: bestPaneIndex,
              contentId: userId,
              contentType: PaneContentType.User,
              targetUserId: userId, // this is to get around the current permissions setup
              breakoutRoomId: breakoutRoom?.id,
            }),
          );
        }
      }
    },
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    (event: Event) => {
      if (!isVisibleInClass || !isAvEnabled || !isInPttMode) return;

      avConnection?.setLocalAudio(false);
      dispatch(setUserIsSpeaking({ userId, isSpeaking: false }));
    },
  );

  useKeyHold(
    ["t"],
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    (event: Event) => {
      if (!canControlClassroom || isInPerson) return;

      if (hasClassStarted) {
        dispatch(
          setKeyboardShortcutSignal({ signal: KeyboardShortcutSignalType.ToggleTalkTimeOverlay }),
        );
      } else {
        createErrorToast(
          "Talk Time Unavailable",
          "Talk time will be available after the class has started.",
        );
      }
    },
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    (event: Event) => {
      if (!canControlClassroom) return;

      dispatch(setKeyboardShortcutSignal({ signal: KeyboardShortcutSignalType.None }));
    },
  );

  return null;
}

export { KeyboardShortcutsController };
